import React from 'react';

const FOCUSABLE = [
  'a[href]', 'area[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', 'iframe', 'audio[controls]', 'video[controls]',
  '[contenteditable]:not([contenteditable="false"])', '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Видимые элементы внутри контейнера, на которые можно перейти по Tab. */
export function getFocusable(root: HTMLElement): HTMLElement[] {
  // Без вёрстки (jsdom в тестах) у элементов нет прямоугольников — тогда
  // видимость не проверяем
  const hasLayout = root.getClientRects().length > 0;
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(el =>
    !el.closest('[hidden], [aria-hidden="true"]')
    && (!hasLayout || el.getClientRects().length > 0));
}

// Стек открытых ловушек: Tab обрабатывает только верхняя (вложенное окно
// подтверждения поверх формы), иначе внешняя перехватывала бы фокус у внутренней
const stack: HTMLElement[] = [];

/**
 * Удержание фокуса внутри модального окна:
 * - при открытии фокус ставится на элемент с [data-autofocus], иначе на
 *   первый фокусируемый, иначе на сам контейнер (ему нужен tabIndex={-1});
 * - Tab / Shift+Tab ходят по кругу внутри окна;
 * - при закрытии фокус возвращается на элемент, который был активен до
 *   открытия (обычно кнопка, открывшая окно).
 *
 * enabled = false — когда поверх открыто вложенное окно: ловушку держит оно.
 */
export function useFocusTrap<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  enabled = true,
): void {
  // Запоминаем активный элемент ещё на рендере, в котором окно открылось:
  // эффекты детей (autoFocus, focus() в полях формы) срабатывают раньше
  // эффекта окна и к тому моменту фокус уже внутри
  const openerRef = React.useRef<HTMLElement | null>(null);
  const wasEnabledRef = React.useRef(false);
  if (enabled && !wasEnabledRef.current && typeof document !== 'undefined') {
    openerRef.current = document.activeElement as HTMLElement | null;
  }
  wasEnabledRef.current = enabled;

  React.useEffect(() => {
    if (!enabled) return undefined;
    const root = ref.current;
    if (!root) return undefined;
    const previous = openerRef.current;
    stack.push(root);

    // Не перехватываем фокус, если он уже внутри (например, autoFocus у поля)
    if (!root.contains(document.activeElement)) {
      const target = root.querySelector<HTMLElement>('[data-autofocus]') ?? getFocusable(root)[0] ?? root;
      target.focus({ preventScroll: true });
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || stack[stack.length - 1] !== root) return;
      const items = getFocusable(root);
      if (items.length === 0) {
        e.preventDefault();
        root.focus({ preventScroll: true });
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || !root.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !root.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const idx = stack.lastIndexOf(root);
      if (idx >= 0) stack.splice(idx, 1);
      // Возвращаем фокус, только если он остался внутри окна или потерялся:
      // если пользователь сам перешёл куда-то ещё — не мешаем
      const active = document.activeElement;
      if (previous && previous.isConnected
        && (!active || active === document.body || root.contains(active) || !root.isConnected)) {
        previous.focus({ preventScroll: true });
      }
    };
  }, [ref, enabled]);
}

/** Верхнее ли это окно среди открытых (для Escape во вложенных окнах). */
export function isTopFocusTrap(root: HTMLElement | null): boolean {
  return !!root && stack[stack.length - 1] === root;
}

/**
 * Поведение модального окна целиком: ловушка фокуса + закрытие по Escape.
 * Escape закрывает только верхнее окно — вложенное подтверждение поверх
 * формы не закроет обе разом.
 *
 * Контейнеру окна нужны role="dialog", aria-modal="true" и tabIndex={-1}.
 */
export function useDialog<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  onClose: () => void,
  enabled = true,
): void {
  useFocusTrap(ref, enabled);
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  React.useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isTopFocusTrap(ref.current)) onCloseRef.current();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [ref, enabled]);
}
